
import {
  computed, defineComponent, onMounted, Ref, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDriveStore } from '../../../../modules/drive/store/driveStore';
import { useFlutterBus } from '@/store/flutterBus';
import BottomPopUpItem from '@/components/BottomPopUp/BottomPopUpItem.vue';
import AppBottomPopUp from '@/components/BottomPopUp/AppBottomPopUp.vue';
import AppLoader from '@/components/UI/AppLoader.vue';
import {
  CHOOSER_TYPE,
  FOLDER_TYPES,
  MEDIA_TYPES,
  NAVIGATION_PAGES,
  UPLOAD_PLACES,
} from '@/types/enums';
import { useMainStore } from '@/store/mainStore';
import {
  IFileUpload, IImageConverted, IImageCrop, ISearchLabelInput,
} from '@/types/interfaces';
import AppTakePhoto from '@/components/AppTakePhoto.vue';
import UseUpload from '@/composables/UseUpload';
import useFlutter from '@/composables/UseFlutter';
import UploadService from '@/services/UploadService';
import { blobToUrl, getVideoThumbnail } from '@/helpers/videoThumbnail';
import isPhoto from '@/helpers/fileDetector';
import { bmpConvertor, bmpConvertorToFile } from '@/helpers/bmpConvertor';

export default defineComponent({
  components: {
    BottomPopUpItem,
    AppBottomPopUp,
    AppLoader,
    AppTakePhoto,
    // AppUploadInfo,
  },
  emits: ['add-case'],
  setup() {
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const flutterBus = useFlutterBus();
    const router = useRouter();
    const route = useRoute();

    const { onUploadUpdate, uploadInfo } = UseUpload();
    const { flutterOpenCamera, flutterOpenImagePicker, flutterOpenFilePicker } = useFlutter();

    const appTakePhoto = ref();

    const fileInputDef = ref();

    // const filesSaving: Ref<boolean> = ref(false);
    const isSaving: Ref<boolean> = ref(false);
    const currentChooserType = ref('');

    const fileInputTUS: Ref<HTMLInputElement | null> = ref(null);
    const mediaToCrop = ref({} as IImageCrop);
    const mediaLoadedKey = ref(0);
    const mediaInputRef: Ref<HTMLInputElement | null> = ref(null);

    const currentPage = computed(() => route.meta.navigationPage);
    const showNewFolder = computed(() => driveStore?.currentFolder?.folderType !== FOLDER_TYPES.CASE_FOLDER);

    const loaderText = computed(() => {
      if (isSaving.value) {
        return 'Updating media';
      }
      return '';
    });

    function setChooserType(chooserType) {
      currentChooserType.value = chooserType;
      const { nativeApp } = window as any;

      if (nativeApp && chooserType) {
        nativeApp.setChooserType(chooserType);
      }
    }

    function hidePopUp() {
      mainStore.BottomPopUpType = null;
    }

    async function splitMedia(mediaList: FileList) {
      const fileList: File[] = [];
      const photoList: File[] = [];

      for (let i = 0; i < mediaList.length; i += 1) {
        if (isPhoto(mediaList[i])) {
          if (mediaList[i].type.includes('bmp')) {
            // eslint-disable-next-line no-await-in-loop
            const convertedFile = await bmpConvertorToFile(mediaList[i]);
            // console.log(convertedFile);
            photoList.push(convertedFile);
          } else {
            photoList.push(mediaList[i]);
          }
        } else {
          fileList.push(mediaList[i]);
        }
      }

      return {
        fileList,
        photoList,
      };
    }

    async function uploadTUS(event: Event) {
      const mediaList: FileList | null = (event.target as HTMLInputElement).files;
      if (mediaList) {
        const { fileList, photoList } = await splitMedia(mediaList);

        const fileUploads = UploadService.toFileUploads(fileList as unknown as FileList);
        const photoUploads = UploadService.toFileUploads(photoList as unknown as FileList);

        const folderId = currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER ? route.params.id as string : undefined;

        if (fileUploads.length) {
          UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: folderId ? UPLOAD_PLACES.FOLDER : UPLOAD_PLACES.MIXED, id: folderId }, folderId);
        }
        if (photoUploads.length) {
          UploadService.batchUploadTUS(photoUploads, onUploadUpdate, { location: folderId ? UPLOAD_PLACES.FOLDER_PHOTO : UPLOAD_PLACES.MIXED_PHOTO, id: folderId }, folderId);
        }

        hidePopUp();
      }
    }

    async function flutterUploadFiles(f: File[]) {
      const fileUploads = UploadService.toFileUploads(f as unknown as FileList);
      const folderId = currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER ? route.params.id as string : undefined;

      if (fileUploads.length) {
        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: folderId ? UPLOAD_PLACES.FOLDER : UPLOAD_PLACES.MIXED, id: folderId }, folderId);
      }
      hidePopUp();
    }

    async function flutterUploadPhotos(f: File[]) {
      const fileUploads = UploadService.toFileUploads(f as unknown as FileList);
      const folderId = currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER ? route.params.id as string : undefined;

      if (fileUploads.length) {
        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: folderId ? UPLOAD_PLACES.FOLDER_PHOTO : UPLOAD_PLACES.MIXED_PHOTO, id: folderId }, folderId);
      }
      hidePopUp();
    }

    watch(() => flutterBus.flutterImagePickerFiles, (nv, ov) => {
      if (nv && nv.length) {
        flutterUploadPhotos(nv);
        flutterBus.flutterImagePickerFiles = null;
      }
    });
    watch(() => flutterBus.flutterFilePickerFiles, (nv, ov) => {
      if (nv && nv.length) {
        flutterUploadFiles(nv);
        flutterBus.flutterFilePickerFiles = null;
      }
    });

    function addCase() {
      const path = currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER ? `/drive/case/create?folderId=${driveStore.currentFolder!.id}` : '/drive/case/create';
      router.push(path);
      // mainStore.openCreateCasePopUp();
      mainStore.BottomPopUpType = null;
    }

    function addFolder() {
      mainStore.openCreateFolderPopUp();
      mainStore.BottomPopUpType = null;
    }

    async function addMedia(e: any) {
      const isBmp = e.target.files ? e.target.files[0].type.includes('bmp') : false;
      const img: File = (e.target as HTMLInputElement).files![0];

      let poster = '' as unknown | undefined | string;
      let converted = {} as IImageConverted;

      if (isBmp) {
        converted = await bmpConvertor(e.target.files[0]);
      }

      if (!isBmp) {
        if (img.type.includes('video')) {
          const thumbnailBlob = await getVideoThumbnail(img, 1.0);
          poster = blobToUrl(thumbnailBlob);
        }
      }

      mediaToCrop.value = {
        url: isBmp ? converted.url : URL.createObjectURL(img),
        fileName: isBmp ? converted.name : img.name,
        fileType: isBmp ? converted.type : img.type,
        poster,
      };
      mediaLoadedKey.value += 1;
      mediaInputRef.value!.value = '';
    }

    async function flutterAddMedia(mediaFile: File) {
      let poster = '' as unknown | undefined | string;

      if (mediaFile.type.includes('video')) {
        // isMediaProcessing.value = true;
        const thumbnailBlob = await getVideoThumbnail(mediaFile, 1.0);
        poster = blobToUrl(thumbnailBlob);
      }
      // isMediaProcessing.value = false;

      mediaToCrop.value = {
        url: URL.createObjectURL(mediaFile),
        fileName: mediaFile.name,
        fileType: mediaFile.type,
        poster,
      };
    }

    watch(() => flutterBus.flutterCameraFile, (newValue) => {
      if (newValue) {
        flutterAddMedia(newValue);
        flutterBus.flutterCameraFile = null;
      }
    });

    function handleMediaCancel() {
      mediaToCrop.value = {} as IImageCrop;
      appTakePhoto.value.resetAppTakePhoto();
      hidePopUp();
    }

    function cancelUpload() {
      UploadService.isAbortUpload = true;
      mainStore.BottomPopUpType = null;
      handleMediaCancel();
    }

    function handleRetake() {
      setChooserType(currentChooserType.value);
      mediaToCrop.value = {} as IImageCrop;
      (mediaInputRef!.value! as HTMLInputElement).click();
    }

    const labels = ref([] as ISearchLabelInput[]);
    async function handleLabels() {
      const response = await mainStore.onSearchInputLabels('');
      labels.value = response;
      return response;
    }

    function handleMultipleUpload(f: any) {
      const folderId = (route.params?.id as string) || undefined;

      const fileUploads = f.file as IFileUpload[];
      onUploadUpdate().setStartInfo(fileUploads);

      handleLabels()
        .then(() => UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: folderId ? UPLOAD_PLACES.FOLDER_PHOTO : UPLOAD_PLACES.MIXED_PHOTO, id: folderId }, folderId, mainStore.recentInputLabels, true));

      hidePopUp();
    }

    onMounted(() => {
      handleLabels();
    });

    return {
      mainStore,
      mediaInputRef,
      fileInputTUS,
      isSaving,
      loaderText,
      mediaToCrop,
      mediaLoadedKey,
      showNewFolder,
      appTakePhoto,
      labels,
      fileInputDef,
      uploadInfo,
      hidePopUp,
      addCase,
      addFolder,
      addMedia,
      handleMediaCancel,
      handleRetake,
      uploadTUS,
      handleMultipleUpload,
      // uploadDefault,
      cancelUpload,
      MEDIA_TYPES,
      FOLDER_TYPES,
      setChooserType,
      flutterOpenFilePicker,
      flutterOpenImagePicker,
      flutterOpenCamera,
      CHOOSER_TYPE,
    };
  },
});
